import { jsPDF } from 'jspdf';

interface EnergyData {
  jahresPotenzial: number;
  einwohner: number;
  haushalte: number;
  energieQuellen: Array<{
    name: string;
    anzahl: number;
    kwh: number;
    potentialEuro: number;
  }>;
}

export async function generatePDFReport(gemeindeName: string, data: EnergyData): Promise<void> {
  const pdf = new jsPDF();
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  
  // Colors
  const primaryColor = '#059669'; // Green-600
  const secondaryColor = '#6b7280'; // Gray-500
  const textColor = '#1f2937'; // Gray-800
  
  let yPosition = 20;
  
  // Header
  pdf.setFillColor(5, 150, 105); // Green background
  pdf.rect(0, 0, pageWidth, 30, 'F');
  
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text('ENERGIEGEMEINSCHAFT POTENZIALANALYSE', pageWidth / 2, 20, { align: 'center' });
  
  yPosition = 45;
  
  // Gemeinde Name
  pdf.setTextColor(textColor);
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`Gemeinde: ${gemeindeName}`, 20, yPosition);
  yPosition += 15;
  
  // Date
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(secondaryColor);
  pdf.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`, 20, yPosition);
  yPosition += 20;
  
  // Main Results Box
  pdf.setFillColor(240, 253, 244); // Light green background
  pdf.setDrawColor(5, 150, 105);
  pdf.setLineWidth(0.5);
  pdf.rect(15, yPosition - 5, pageWidth - 30, 40, 'FD');
  
  pdf.setTextColor(primaryColor);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text('JÄHRLICHES POTENZIAL', 20, yPosition + 5);
  
  pdf.setFontSize(24);
  pdf.text(`€${data.jahresPotenzial.toLocaleString('de-DE')}`, 20, yPosition + 20);
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`€${Math.round(data.jahresPotenzial / data.einwohner)} pro Einwohner`, 20, yPosition + 30);
  pdf.text(`€${Math.round(data.jahresPotenzial / data.haushalte)} pro Haushalt`, 100, yPosition + 30);
  
  yPosition += 55;
  
  // Gemeinde Information
  pdf.setTextColor(textColor);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('GEMEINDEDATEN', 20, yPosition);
  yPosition += 10;
  
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'normal');
  pdf.text(`Einwohner: ${data.einwohner.toLocaleString('de-DE')}`, 20, yPosition);
  pdf.text(`Haushalte: ${data.haushalte.toLocaleString('de-DE')}`, 100, yPosition);
  yPosition += 20;
  
  // Energy Sources
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('ENERGIEQUELLEN', 20, yPosition);
  yPosition += 15;
  
  // Table Header
  pdf.setFillColor(247, 250, 252);
  pdf.rect(15, yPosition - 5, pageWidth - 30, 10, 'F');
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Energieträger', 20, yPosition);
  pdf.text('Anlagen', 80, yPosition);
  pdf.text('Produktion (kWh)', 120, yPosition);
  pdf.text('Potenzial (€)', 170, yPosition);
  yPosition += 15;
  
  // Table Content
  pdf.setFont('helvetica', 'normal');
  data.energieQuellen.forEach((source, index) => {
    if (yPosition > pageHeight - 40) {
      pdf.addPage();
      yPosition = 20;
      
      // Add header for new page
      pdf.setFillColor(5, 150, 105); // Green background
      pdf.rect(0, 0, pageWidth, 20, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`${gemeindeName} - Energiequellen (Fortsetzung)`, pageWidth / 2, 13, { align: 'center' });
      
      yPosition = 35;
      
      // Re-add table header
      pdf.setFillColor(247, 250, 252);
      pdf.rect(15, yPosition - 5, pageWidth - 30, 10, 'F');
      
      pdf.setTextColor(textColor);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Energieträger', 20, yPosition);
      pdf.text('Anlagen', 80, yPosition);
      pdf.text('Produktion (kWh)', 120, yPosition);
      pdf.text('Potenzial (€)', 170, yPosition);
      yPosition += 15;
      
      pdf.setFont('helvetica', 'normal');
    }
    
    // Alternating row colors
    if (index % 2 === 0) {
      pdf.setFillColor(249, 250, 251);
      pdf.rect(15, yPosition - 5, pageWidth - 30, 10, 'F');
    }
    
    pdf.setTextColor(textColor); // Ensure text color is reset
    pdf.text(source.name, 20, yPosition);
    pdf.text(source.anzahl.toString(), 80, yPosition);
    pdf.text(source.kwh.toLocaleString('de-DE'), 120, yPosition);
    pdf.text(`€${source.potentialEuro.toLocaleString('de-DE')}`, 170, yPosition);
    yPosition += 12;
  });
  
  yPosition += 10;
  
  // Calculation Formula
  // Check if we need a new page
  if (yPosition > pageHeight - 60) {
    pdf.addPage();
    yPosition = 20;
    
    // Add header for new page
    pdf.setFillColor(5, 150, 105); // Green background
    pdf.rect(0, 0, pageWidth, 20, 'F');
    
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${gemeindeName} - Berechnungsdetails`, pageWidth / 2, 13, { align: 'center' });
    
    yPosition = 35;
  }
  
  pdf.setTextColor(textColor); // Ensure text color is reset
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('BERECHNUNGSGRUNDLAGE', 20, yPosition);
  yPosition += 15;
  
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  pdf.text('Potenzial = Gesamtproduktion × 40% (handelbar) × 0,106 €/kWh', 20, yPosition);
  yPosition += 8;
  pdf.text('40% der produzierten Energie kann durchschnittlich in der', 20, yPosition);
  yPosition += 8;
  pdf.text('Energiegemeinschaft gehandelt werden.', 20, yPosition);
  yPosition += 8;
  pdf.text('0,106 €/kWh entspricht dem durchschnittlichen Marktpreis für', 20, yPosition);
  yPosition += 8;
  pdf.text('regional erzeugte erneuerbare Energie.', 20, yPosition);
  
  // Footer
  const footerY = pageHeight - 20;
  pdf.setFontSize(8);
  pdf.setTextColor(secondaryColor);
  pdf.text('Erstellt mit Bürgerstrom Dashboard | www.buergerstrom.at', pageWidth / 2, footerY, { align: 'center' });
  
  // Save the PDF
  pdf.save(`energiegemeinschaft-${gemeindeName.toLowerCase().replace(/\s/g, '-')}.pdf`);
}